import { Chip, Css, Tag, useTestIds } from "@homebound/beam";
import { useMemo } from "react";
import { pluralize } from "src/utils";
import { bidPlanGroupDimensionKey, optionMatchesDimension, orderedBidPlanGroupDimensions } from "../utils";
import { PlanBasedBidsTableV2_BidPlanGroupFragment } from "./PlanBasedBidsTableV2.generated";

// Renders a section header for a bid plan group: the Base Configuration / Add-On tag, a title, and a chip per
// dimension summarizing its options. The title is passed in (`displayName`) so this is shared between the
// cost-entry table (uses the child group's name) and the scope table (uses the bid plan group's name).
export function PlanBasedBidsTableHeader(props: {
  bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment;
  displayName: string;
}) {
  const { bidPlanGroup, displayName } = props;
  const tid = useTestIds(props, "tableHeader");
  const isAddOn = !!bidPlanGroup.globalOption;
  const optionNamesByDimension = useMemo(() => {
    const allOptions = bidPlanGroup.childGroups.flatMap((cg) => cg.lines.flatMap((line) => line.options));
    return bidPlanGroup.dimensions.mapToObject((dim) => {
      const names = allOptions
        .filter((opt) => optionMatchesDimension(opt.globalOption, dim))
        .map((opt) => opt.globalOption)
        .uniqueByKey("id")
        .map((go) => go.name);
      return [bidPlanGroupDimensionKey(dim), names];
    });
  }, [bidPlanGroup]);
  // Lead the summary chips with the personalization options, then the type-based price dimensions
  const ordered = orderedBidPlanGroupDimensions(bidPlanGroup.dimensions);
  const chipDimensions = [
    ...ordered.filter((d) => d.globalOptionGroup),
    ...ordered.filter((d) => !d.globalOptionGroup),
  ];

  return (
    <div {...tid} css={Css.df.fdc.gap1.px2.py1.bgWhite.bb.bcGray200.$}>
      <div>
        <Tag type={isAddOn ? "info" : "neutral"} text={isAddOn ? "Add-On" : "Base Configuration"} />
      </div>
      <div css={Css.df.aic.gap2.$}>
        <span {...tid.title} css={Css.mdSb.gray900.$}>
          {displayName}
        </span>
        {chipDimensions.map((dim) => {
          const names = optionNamesByDimension[bidPlanGroupDimensionKey(dim)] ?? [];
          // Skip rendering the chip when none of this POF tab's lines reference the dimension's options
          if (names.isEmpty) return null;
          return (
            <Chip
              key={dim.id}
              text={`${names.length} ${pluralize(names.length, dim.name === displayName ? "Option" : dim.name)}`}
              title={
                <div css={Css.df.fdc.$}>
                  {names.map((n) => (
                    <span key={n}>{n}</span>
                  ))}
                </div>
              }
            />
          );
        })}
      </div>
    </div>
  );
}
